import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { CreditCounter } from './CreditComponents';
import logo from '../assets/logo.png';

// Stripe Payment Links (base URLs) - Updated April 2026
const STRIPE_LINKS = {
  pro_monthly: 'https://buy.stripe.com/3cI6oH52i1q0b821Db1440j',
  all_access_lifetime: 'https://buy.stripe.com/cNi28r0M29Wwfoi1Db1440i',
  basic_pack: 'https://buy.stripe.com/dRm9ATgL06Kkfoigy51440k'
};

const Pricing = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  // Get promo code from URL
  const promoCode = searchParams.get('promo');
  const [showPromoBanner, setShowPromoBanner] = useState(!!promoCode);

  // Handle hash navigation for FAQ section
  useEffect(() => {
    if (location.hash === '#faq') {
      const faqElement = document.getElementById('faq');
      if (faqElement) {
        setTimeout(() => {
          faqElement.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [location]);

  // Build Stripe URL with promo code and prefilled email
  const getStripeUrl = (baseUrl) => {
    let url = baseUrl;
    const params = [];
    
    // Add promo code if present
    if (promoCode) {
      params.push(`prefilled_promo_code=${encodeURIComponent(promoCode)}`);
    }
    
    // Add user email if logged in
    if (user?.email) {
      params.push(`prefilled_email=${encodeURIComponent(user.email)}`);
    }
    
    if (params.length > 0) {
      url += (url.includes('?') ? '&' : '?') + params.join('&');
    }
    
    return url;
  };

  const handleGetStarted = (plan) => {
    if (plan === 'free') {
      navigate('/tracker');
    } else if (plan === 'pro_monthly') {
      window.location.href = getStripeUrl(STRIPE_LINKS.pro_monthly);
    } else if (plan === 'all_access_lifetime') {
      window.location.href = getStripeUrl(STRIPE_LINKS.all_access_lifetime);
    } else {
      navigate('/tracker');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Promo Code Banner */}
      {showPromoBanner && promoCode && (
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white py-3 px-4">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="font-medium">
                Promo code <span className="font-bold">{promoCode}</span> will be applied at checkout!
              </span>
            </div>
            <button 
              onClick={() => setShowPromoBanner(false)}
              className="text-white/80 hover:text-white"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Navigation - Simplified */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-3">
              <img src={logo} alt="AlignifyNOW" className="h-[60px] w-auto" />
              <span className="text-xl font-bold tracking-tight text-slate-900 hidden sm:inline">
                Alignify<span className="text-blue-600">NOW</span>
              </span>
            </Link>
            <div className="flex items-center space-x-6">
              <Link to="/" className="text-sm font-medium text-gray-600 hover:text-gray-900 hidden sm:block">
                Tool Hub
              </Link>
              <Link to="/tracker" className="text-sm font-medium text-gray-600 hover:text-gray-900 hidden sm:block">
                Job Tracker
              </Link>
              <span className="text-sm font-medium text-blue-600 border-b-2 border-blue-600 pb-1 hidden sm:block">
                Pricing
              </span>
              
              <CreditCounter className="hidden sm:flex" />
              
              {isAuthenticated ? (
                <Link
                  to="/"
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Dashboard
                </Link>
              ) : (
                <Link
                  to="/tracker"
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Sign In
                </Link>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Pricing Header */}
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              Simple, Transparent Pricing
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto">
              Start for free. Upgrade when you need more power.
            </p>
          </div>

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            
            {/* Free Starter */}
            <div className="relative bg-white rounded-2xl border border-gray-200 p-8 shadow-sm hover:shadow-lg transition-shadow">
              <div className="mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Free to Start</h3>
                <div className="flex items-baseline">
                  <span className="text-4xl font-bold text-gray-900">$0</span>
                  <span className="text-gray-500 ml-2">/forever</span>
                </div>
              </div>

              <ul className="space-y-4 mb-6">
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-700"><strong>3 Free Credits</strong> to start</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-700"><strong>20 Active</strong> Job Slots</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-700">Match Score (1 credit)</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-700">Mock Interview (1 credit)</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-purple-500 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  <span className="text-gray-700">
                    <strong>Future-Proof Audit</strong>{' '}
                    (<span className="text-gray-400 line-through">8 credits</span>{' '}
                    <span className="text-purple-700 font-semibold">5 credits</span>)
                  </span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-700">Cover Letter Starter</span>
                </li>
              </ul>

              <div className="space-y-3">
                <button
                  onClick={() => handleGetStarted('free')}
                  className="w-full py-3 px-6 bg-gray-100 text-gray-800 font-semibold rounded-xl hover:bg-gray-200 transition-colors"
                >
                  Get Started Free
                </button>
                <a
                  href={getStripeUrl(STRIPE_LINKS.basic_pack)}
                  className="block w-full py-3 px-6 text-center text-blue-600 font-semibold rounded-xl border-2 border-blue-500 hover:bg-blue-50 hover:border-blue-600 transition-colors"
                >
                  Buy 10 Credits for $5
                </a>
              </div>
            </div>

            {/* Pro Monthly */}
            <div className="relative bg-white rounded-2xl border-2 border-blue-500 p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="px-4 py-1 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-semibold rounded-full">
                  Most Popular
                </span>
              </div>

              <div className="mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Pro Monthly</h3>
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-bold text-gray-900">$29</span>
                  <span className="text-gray-500">/month</span>
                </div>
                <p className="text-sm text-blue-600 font-medium mt-1">Cancel anytime</p>
              </div>

              <ul className="space-y-4 mb-6">
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-700"><strong>50 Premium Credits/month</strong></span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-700"><strong>Unlimited Match Score Scans</strong></span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-700"><strong>Unlimited</strong> Job Tracking</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-700"><strong>Pro Analytics</strong> & Insights</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-purple-500 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  <span className="text-gray-700"><strong>Future-Proof Audit</strong> included</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-700">Mock Interviews (1 credit each)</span>
                </li>
              </ul>

              <button
                onClick={() => handleGetStarted('pro_monthly')}
                className="w-full py-3 px-6 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg"
              >
                Start Pro Monthly
              </button>
            </div>

            {/* All-Access Lifetime */}
            <div className="relative bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl border-2 border-purple-400 p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="px-4 py-1 bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-sm font-semibold rounded-full">
                  ⏰ Best Value
                </span>
              </div>

              <div className="mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">All-Access Lifetime</h3>
                <div className="flex items-baseline gap-2">
                  <span className="text-lg text-gray-400 line-through">$397</span>
                  <span className="text-4xl font-bold text-purple-900">$197</span>
                  <span className="text-gray-500">one-time</span>
                </div>
                <p className="text-sm text-purple-600 font-medium mt-1">Save $200 - Pay Once, Use Forever</p>
              </div>

              <ul className="space-y-4 mb-8">
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-purple-500 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-700"><strong>50 Premium Credits/month</strong> (Auto-refills)</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-purple-500 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-700"><strong>Unlimited Match Score Scans</strong></span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-purple-500 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-700"><strong>Unlimited</strong> Job Tracking</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-purple-500 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-700"><strong>Future-Proof Audit</strong> included</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-purple-500 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-700"><strong>All Future Features</strong> Included</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-purple-500 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-700"><strong>Priority Support</strong></span>
                </li>
              </ul>

              <button
                onClick={() => handleGetStarted('all_access_lifetime')}
                className="w-full py-3 px-6 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold rounded-xl hover:from-purple-700 hover:to-indigo-700 transition-all shadow-lg"
              >
                Get Lifetime Access
              </button>
            </div>
          </div>

          {/* Value Comparison */}
          <div className="mt-12 max-w-3xl mx-auto">
            <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-4 text-center">Premium Credits vs Pay-as-you-go</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="p-4 bg-gray-50 rounded-xl">
                  <h4 className="font-semibold text-gray-700 mb-2">Pay-as-you-go</h4>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li>• 10 credits for $5</li>
                    <li>• No commitment</li>
                    <li>• Buy when you need</li>
                    <li>• Good for light users</li>
                  </ul>
                </div>
                <div className="p-4 bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl border border-blue-200">
                  <h4 className="font-semibold text-blue-700 mb-2">Premium (Pro/Lifetime)</h4>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li>• <strong>50 credits/month</strong> ($25 value included)</li>
                    <li>• <strong>Unlimited Match Scores</strong></li>
                    <li>• Unlimited Job Tracking</li>
                    <li>• Best for active job seekers</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Trust Signals Section */}
          <div className="mt-12 space-y-6">
            {/* Secure Payment Badge */}
            <div className="flex flex-wrap justify-center items-center gap-6 text-gray-500">
              <div className="flex items-center gap-2">
                <svg className="w-8 h-8 text-gray-400" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/>
                </svg>
                <span className="text-sm font-medium">SSL Secured</span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-8 h-8" viewBox="0 0 60 25" fill="none">
                  <rect width="60" height="25" rx="4" fill="#635BFF"/>
                  <path d="M13.5 10.5c0-1.1.9-1.5 2.4-1.5 2.1 0 4.8.6 6.9 1.8V5.4c-2.3-.9-4.6-1.3-6.9-1.3-5.6 0-9.4 2.9-9.4 7.8 0 7.6 10.5 6.4 10.5 9.7 0 1.3-1.1 1.7-2.7 1.7-2.3 0-5.3-.9-7.7-2.2v5.5c2.6 1.1 5.3 1.6 7.7 1.6 5.8 0 9.8-2.9 9.8-7.8-.1-8.2-10.6-6.8-10.6-9.9z" fill="white"/>
                </svg>
                <span className="text-sm font-medium">Powered by Stripe</span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <span className="text-sm font-medium">256-bit Encryption</span>
              </div>
            </div>

            {/* Risk-Free Guarantee */}
            <div className="text-center">
              <div className="inline-flex items-center px-6 py-3 bg-green-50 border border-green-200 rounded-xl">
                <span className="text-2xl mr-3">🛡️</span>
                <div className="text-left">
                  <p className="font-semibold text-green-800">Risk-Free Guarantee</p>
                  <p className="text-sm text-green-700">
                    Our Lifetime Access plan comes with a 7-day money-back guarantee. If it doesn&apos;t help your search, email us for a full refund.
                  </p>
                </div>
              </div>
            </div>

            {/* Instant Access Note */}
            <p className="text-center text-sm text-gray-600">
              <svg className="w-4 h-4 inline mr-1 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <strong>Instant Access</strong> — Credits and features activate immediately after payment
            </p>
          </div>

          {/* Fair Use Policy */}
          <div className="mt-8 text-center max-w-2xl mx-auto px-4">
            <p className="text-sm text-gray-700 leading-relaxed">
              <strong>Fair Use Policy:</strong> &quot;Unlimited Match Score Scans&quot; are intended for reasonable personal use by a single job seeker. To ensure quality for everyone, accounts exceeding 50 scans/day or exhibiting automated behavior (bots/scraping) may be subject to review or throttling.
            </p>
          </div>
        </div>
      </section>

      {/* Free Resource Section */}
      <section className="py-12 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="flex-shrink-0">
                <div className="w-20 h-20 bg-green-500/20 rounded-2xl flex items-center justify-center">
                  <span className="text-4xl">🌱</span>
                </div>
              </div>
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-2xl font-bold text-white mb-2">
                  Not Ready to Commit? Start Here.
                </h3>
                <p className="text-slate-300 mb-4">
                  Download our free <strong>AlignifyNOW Reset!</strong> — a human-centered guide 
                  to reclaiming clarity, confidence, and direction. No credit card required.
                </p>
                <Link 
                  to="/checklist"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white text-slate-900 font-semibold rounded-lg hover:bg-slate-100 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Get Free Checklist
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h3 className="font-semibold text-gray-900 mb-2">How do credits work?</h3>
              <p className="text-gray-600">
                <strong>Match Score</strong> and <strong>Mock Interview</strong> sessions cost 1 credit each. 
                The <strong>Future-Proof AI Audit</strong> normally costs <span className="line-through text-gray-400">8 credits</span> — <span className="font-semibold text-purple-700">5 credits</span> during our launch. <strong>Your first audit is on us</strong> — new users get 5 free credits at signup.
                Need more? Buy a 10-credit pack for $5, or go Pro/Lifetime for 50 credits/month plus unlimited Match Scores.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h3 className="font-semibold text-gray-900 mb-2">What counts as an &quot;active&quot; job slot?</h3>
              <p className="text-gray-600">
                Active jobs are those with status: Applied, Interview, Offer, or On Hold. 
                Archived, Rejected, and Withdrawn jobs don&apos;t count toward your limit—so you can 
                track your full job history without restrictions.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h3 className="font-semibold text-gray-900 mb-2">Can I cancel anytime?</h3>
              <p className="text-gray-600">
                Yes! Pro Monthly can be cancelled at any time. Your access continues until the end 
                of your billing period. Lifetime Access is a one-time purchase—no recurring charges ever.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h3 className="font-semibold text-gray-900 mb-2">What payment methods do you accept?</h3>
              <p className="text-gray-600">
                We accept all major credit cards, debit cards, and Apple Pay through our secure 
                Stripe payment processor.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h3 className="font-semibold text-gray-900 mb-2">Is my data secure?</h3>
              <p className="text-gray-600">
                Absolutely. We use industry-standard encryption and our &quot;Zero-Retention Guarantee&quot; 
                means your resume data is processed in memory and never stored permanently.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h3 className="font-semibold text-gray-900 mb-2">Why isn&apos;t my resume saved to my Google Drive?</h3>
              <p className="text-gray-600">
                We practice <strong>Strict Zero-Retention</strong>. We do not auto-upload or backup your files. 
                We analyze them instantly and then discard the data to protect your privacy. Please keep your original files safe.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center mb-4 md:mb-0">
              <span className="text-xl font-bold text-white opacity-80">
                Alignify<span className="text-blue-400">NOW</span>
              </span>
            </div>
            <div className="text-gray-400 text-xs mb-4 md:mb-0">
              © 2026 AlignifyNOW. All rights reserved. Built by Matambanadzo Studio.
            </div>
            <div className="flex items-center space-x-6">
              <Link to="/privacy" className="text-gray-400 hover:text-white text-sm transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-gray-400 hover:text-white text-sm transition-colors">
                Terms of Service
              </Link>
              <Link to="/blog" className="text-gray-400 hover:text-white text-sm transition-colors">
                Blog
              </Link>
              <Link to="/affiliates" className="text-gray-400 hover:text-white text-sm transition-colors">
                Affiliates
              </Link>
              <Link to="/checklist" className="text-gray-400 hover:text-white text-sm transition-colors">
                Free Checklist
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Pricing;
